import type { AgentAskOptions, AgentHealth, AgentResponse, AgentStreamEvent } from "./types";

export class AuthRequiredError extends Error {
  constructor() {
    super("Login required.");
    this.name = "AuthRequiredError";
  }
}

export async function login(username: string, password: string) {
  const response = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password })
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error || "Login failed.");
  }

  return payload as { ok: true; user: string };
}

export async function logout() {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "include"
  });
}

export async function getAuthSession() {
  const response = await fetch("/api/auth/session", {
    credentials: "include"
  });
  if (!response.ok) return { authenticated: false, user: null };
  return response.json() as Promise<{ authenticated: boolean; user: string | null }>;
}

export async function askMedPathAgent(question: string, options: AgentAskOptions): Promise<AgentResponse> {
  const response = await fetch("/api/agent/ask", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      model: options.model,
      personalNotes: options.personalNotes,
      history: options.history || []
    })
  });

  const payload = await response.json().catch(() => null);
  if (response.status === 401) {
    throw new AuthRequiredError();
  }
  if (!response.ok) {
    const message = payload?.error || `Agent request failed with HTTP ${response.status}`;
    throw new Error(message);
  }

  return payload as AgentResponse;
}

export async function askMedPathAgentStream(
  question: string,
  options: AgentAskOptions,
  handlers: {
    onStatus?: (message: string) => void;
    onDelta?: (text: string) => void;
  } = {}
): Promise<AgentResponse> {
  const response = await fetch("/api/agent/ask-stream", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      model: options.model,
      personalNotes: options.personalNotes,
      history: options.history || []
    })
  });

  if (response.status === 401) {
    throw new AuthRequiredError();
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.error || `Agent request failed with HTTP ${response.status}`);
  }

  if (!response.body) {
    const result = await askMedPathAgent(question, options);
    handlers.onDelta?.(result.answer);
    return result;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: AgentResponse | null = null;

  function handleLine(line: string) {
    if (!line.trim()) return;
    const event = JSON.parse(line) as AgentStreamEvent;
    if (event.type === "status") {
      handlers.onStatus?.(event.message);
      return;
    }
    if (event.type === "delta") {
      handlers.onDelta?.(event.text);
      return;
    }
    if (event.type === "result") {
      result = event.result;
      return;
    }
    if (event.type === "error") {
      throw new Error(event.error);
    }
  }

  while (true) {
    const { done, value } = await reader.read();
    if (value) {
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) handleLine(line);
    }
    if (done) break;
  }

  buffer += decoder.decode();
  if (buffer.trim()) handleLine(buffer);
  if (!result) throw new Error("The assistant stream ended before returning a result.");
  return result;
}

export async function getAgentHealth() {
  const response = await fetch("/api/agent/health", {
    credentials: "include"
  });
  if (!response.ok) return null;
  return response.json() as Promise<AgentHealth>;
}
